// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger

function DialogDescription({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <DialogPrimitive.Description className={cn('text-sm text-muted-foreground', className)}>
      {children}
    </DialogPrimitive.Description>
  )
}

interface DialogContentProps {
  title: string
  children: React.ReactNode
  className?: string
  /** When false, the dialog can only be closed via the X button — not by
   *  clicking the overlay or pressing Escape. Defaults to true. */
  dismissible?: boolean
}

function DialogContent({ title, children, className, dismissible = true }: DialogContentProps) {
  const lockProps = dismissible
    ? {}
    : {
        onPointerDownOutside: (e: Event) => e.preventDefault(),
        onInteractOutside: (e: Event) => e.preventDefault(),
        onEscapeKeyDown: (e: KeyboardEvent) => e.preventDefault(),
      }
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="bg-black/50 fixed inset-0 z-40" />
      <DialogPrimitive.Content
        {...lockProps}
        className={cn(
          'bg-card border border-border rounded-3xl shadow-2xl p-8 w-full max-w-lg',
          'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50',
          'focus:outline-none max-h-[90vh] overflow-y-auto',
          className
        )}
      >
        <div className="flex items-center justify-between mb-6">
          <DialogPrimitive.Title className="text-lg font-bold text-foreground">
            {title}
          </DialogPrimitive.Title>
          <DialogPrimitive.Close className="rounded-full p-2 hover:bg-secondary transition-colors">
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>
        </div>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

export { Dialog, DialogTrigger, DialogContent, DialogDescription }
